import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  FormGroup,
  Input,
  Label,
} from "reactstrap";

import { useFetchLocationsQuery } from "../api/cmsPanelsApi";
import { getApiErrorMessage } from "../api/errorUtils";
import {
  useFetchReturnByIdQuery,
  useFetchReturnsListQuery,
} from "../api/returnsApi";
import { printReturnRefundSlip } from "../components/Print/commerceDocumentPrint";
import SaleItemLabel from "../components/Sales/SaleItemLabel";
import { useTranslation } from "../hooks/useTranslation";
import { formatMoney, formatTime, todayString, useDebounce } from "./salesUtils";

const COL = {
  number: { width: 100, flexShrink: 0 },
  sale: { width: 92, flexShrink: 0 },
  items: { width: 44, flexShrink: 0, textAlign: "right" },
  time: { width: 48, flexShrink: 0 },
  location: { width: 88, flexShrink: 0 },
  amount: { width: 88, flexShrink: 0, textAlign: "right" },
};

const RecentReturnsPage = () => {
  const t = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedDate, setSelectedDate] = useState(
    String(searchParams.get("date") || "").trim() || todayString()
  );
  const [locationId, setLocationId] = useState(
    String(searchParams.get("locationId") || "").trim()
  );
  const [returnNumberInput, setReturnNumberInput] = useState(
    String(searchParams.get("returnNumber") || "").trim()
  );
  const [selectedReturnId, setSelectedReturnId] = useState(() => {
    const value = Number(searchParams.get("returnId"));
    return Number.isInteger(value) && value > 0 ? value : null;
  });

  const debouncedReturnNumber = useDebounce(returnNumberInput, 350);

  const { data: locations = [], isFetching: isFetchingLocations } =
    useFetchLocationsQuery();
  const {
    data: returnsList,
    isFetching: isFetchingReturns,
    error: returnsListError,
  } = useFetchReturnsListQuery({
    date: selectedDate,
    locationId: locationId || undefined,
    returnNumber: debouncedReturnNumber || undefined,
    pageSize: 30,
  });
  const {
    data: selectedReturn,
    isFetching: isFetchingReturnDetail,
    error: returnDetailError,
  } = useFetchReturnByIdQuery(selectedReturnId, {
    skip: !selectedReturnId,
  });

  useEffect(() => {
    if (!returnsList?.items?.length) {
      setSelectedReturnId(null);
      return;
    }
    if (selectedReturnId) {
      const stillVisible = returnsList.items.some((item) => item.id === selectedReturnId);
      if (!stillVisible) {
        setSelectedReturnId(null);
      }
    }
  }, [returnsList, selectedReturnId]);

  useEffect(() => {
    const nextParams = new URLSearchParams();

    if (selectedDate) nextParams.set("date", selectedDate);
    if (locationId) nextParams.set("locationId", locationId);
    if (debouncedReturnNumber) nextParams.set("returnNumber", debouncedReturnNumber);
    if (selectedReturnId) nextParams.set("returnId", String(selectedReturnId));

    const nextQuery = nextParams.toString();
    const currentQuery = searchParams.toString();
    if (nextQuery !== currentQuery) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [
    locationId,
    debouncedReturnNumber,
    searchParams,
    selectedDate,
    selectedReturnId,
    setSearchParams,
  ]);

  const selectedReturnSummary = useMemo(
    () => returnsList?.items?.find((item) => item.id === selectedReturnId) || null,
    [returnsList, selectedReturnId]
  );

  const drawerOpen = Boolean(selectedReturnId);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape" && selectedReturnId) setSelectedReturnId(null);
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [selectedReturnId]);

  const clearFilters = () => {
    setSelectedDate(todayString());
    setLocationId("");
    setReturnNumberInput("");
  };

  const hasActiveFilters =
    Boolean(locationId) ||
    Boolean(returnNumberInput.trim()) ||
    selectedDate !== todayString();

  return (
    <div className="content sales-premium sales-page history-page recent-returns-page">
      <Card className="sales-page-card">
        <CardHeader className="sales-page-header">
          <div>
            <div className="sp-title">{t("recentReturns.title")}</div>
            <p className="sp-subtitle">{t("recentReturns.subtitle")}</p>
          </div>
          <div className="sales-page-header__actions">
            {hasActiveFilters ? (
              <Button color="secondary" size="sm" type="button" onClick={clearFilters}>
                {t("products.resetFilters")}
              </Button>
            ) : null}
            <span className="sp-badge">
              {t("commerceCommon.resultsCount").replace("{count}", returnsList?.total ?? 0)}
            </span>
          </div>
        </CardHeader>

        <CardBody className="sales-page-body">
          {returnsListError ? (
            <Alert color="danger" className="sp-feedback-alert">
              {getApiErrorMessage(returnsListError, t("recentReturns.loadError"))}
            </Alert>
          ) : null}

          <div className="sp-filters-compact">
            <FormGroup>
              <Label for="recent-returns-date">{t("commerceCommon.date")}</Label>
              <Input
                id="recent-returns-date"
                type="date"
                value={selectedDate}
                onChange={(event) => setSelectedDate(event.target.value)}
                disabled={isFetchingReturns}
              />
            </FormGroup>
            <FormGroup>
              <Label for="recent-returns-location">{t("commerceCommon.location")}</Label>
              <Input
                id="recent-returns-location"
                type="select"
                value={locationId}
                onChange={(event) => setLocationId(event.target.value)}
                disabled={isFetchingLocations || isFetchingReturns}
              >
                <option value="">{t("commerceCommon.allLocations")}</option>
                {locations.map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name ||
                      t("commerceCommon.locationFallback").replace("{id}", location.id)}
                  </option>
                ))}
              </Input>
            </FormGroup>
            <FormGroup>
              <Label for="recent-returns-number">{t("commerceCommon.returnNumber")}</Label>
              <Input
                id="recent-returns-number"
                type="text"
                autoComplete="off"
                placeholder={t("commerceCommon.typeToFilter")}
                value={returnNumberInput}
                onChange={(event) => setReturnNumberInput(event.target.value)}
                disabled={isFetchingReturns}
              />
            </FormGroup>
          </div>

          <div className="sp-list-header">
            <span style={COL.number}>{t("commerceCommon.returnNumber")}</span>
            <span style={COL.sale}>{t("commerceCommon.fromSale")}</span>
            <span style={COL.items}>{t("commerceCommon.qty")}</span>
            <span style={COL.time}>{t("commerceCommon.time")}</span>
            <span style={COL.location} className="sp-hide-sm">
              {t("commerceCommon.location")}
            </span>
            <span style={COL.amount}>{t("commerceCommon.refund")}</span>
          </div>

          {isFetchingReturns ? (
            <div className="sp-list-empty">
              <span className="sp-text-sm sp-text-muted">{t("commerceCommon.loading")}</span>
            </div>
          ) : returnsList?.items?.length ? (
            <div className="sp-sale-list">
              {returnsList.items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`sp-sale-row${selectedReturnId === item.id ? " active" : ""}`}
                  onClick={() => setSelectedReturnId(item.id)}
                  aria-label={`${t("commerceCommon.returnDetail")} ${item.returnNumber}`}
                >
                  <span style={COL.number} className="sp-font-semibold">
                    {item.returnNumber}
                  </span>
                  <span style={COL.sale} className="sp-text-muted sp-truncate">
                    {item.originalSale?.saleNumber || "—"}
                  </span>
                  <span style={COL.items} className="sp-text-dim sp-text-sm">
                    {item.lineCount}
                  </span>
                  <span style={COL.time} className="sp-text-dim sp-text-sm">
                    {formatTime(item.completedAt)}
                  </span>
                  <span
                    style={COL.location}
                    className="sp-text-dim sp-text-sm sp-hide-sm sp-truncate"
                  >
                    {item.location?.name || "—"}
                  </span>
                  <span style={COL.amount} className="sp-font-semibold">
                    {formatMoney(item.totalRefundAmount)}
                  </span>
                </button>
              ))}
            </div>
          ) : (
            <div className="sp-list-empty">
              <span className="sp-text-sm sp-text-muted">
                {t("recentReturns.noReturnsFound")}
              </span>
            </div>
          )}
        </CardBody>
      </Card>

      <div
        className={`sp-drawer-backdrop${drawerOpen ? " open" : ""}`}
        onClick={() => setSelectedReturnId(null)}
      />

      <div className={`sp-drawer${drawerOpen ? " open" : ""}`}>
        <div className="sp-drawer-header">
          <div>
            <div className="sp-title">{t("commerceCommon.returnDetail")}</div>
            <p className="sp-subtitle">
              {selectedReturn?.returnNumber || selectedReturnSummary?.returnNumber || ""}
              {selectedReturn?.status ? (
                <span className="sp-status-badge sp-status-badge--completed">
                  {selectedReturn.status}
                </span>
              ) : null}
            </p>
          </div>
          <button
            type="button"
            className="sp-drawer-close"
            onClick={() => setSelectedReturnId(null)}
            aria-label={t("commerceCommon.close")}
          >
            ×
          </button>
        </div>

        <div className="sp-drawer-body">
          {returnDetailError ? (
            <Alert color="danger" className="sp-feedback-alert">
              {getApiErrorMessage(returnDetailError, t("recentReturns.detailLoadError"))}
            </Alert>
          ) : isFetchingReturnDetail ? (
            <span className="sp-text-sm sp-text-muted">{t("commerceCommon.loading")}</span>
          ) : selectedReturn ? (
            <>
              <div className="sp-drawer-actions">
                <Button
                  color="secondary"
                  size="sm"
                  onClick={() => printReturnRefundSlip(selectedReturn)}
                >
                  {t("commerceCommon.viewPrintRefundSlip")}
                </Button>
                {selectedReturn.originalSale?.saleNumber ? (
                  <Button
                    tag={Link}
                    color="secondary"
                    size="sm"
                    to={`/admin/sales/history?date=${encodeURIComponent(
                      String(selectedReturn.originalSale.completedAt || "").slice(0, 10)
                    )}&saleNumber=${encodeURIComponent(
                      selectedReturn.originalSale.saleNumber
                    )}&saleId=${encodeURIComponent(selectedReturn.originalSale.id)}`}
                  >
                    {t("commerceCommon.openLinkedSale")}
                  </Button>
                ) : null}
              </div>

              <div className="sales-page-completed-meta">
                <div className="sales-page-completed-meta__item">
                  <span className="sp-stat-label">{t("commerceCommon.fromSale")}</span>
                  <span className="sp-stat-value">
                    {selectedReturn.originalSale?.saleNumber || "—"}
                  </span>
                </div>
                <div className="sales-page-completed-meta__item">
                  <span className="sp-stat-label">{t("commerceCommon.refund")}</span>
                  <span className="sp-stat-value text-capitalize">
                    {selectedReturn.refundMethod || t("commerceCommon.exchangeFallback")}
                  </span>
                </div>
                <div className="sales-page-completed-meta__item sales-page-completed-meta__item--total">
                  <span className="sp-stat-label">{t("commerceCommon.totalRefunded")}</span>
                  <span className="sp-stat-value">
                    {formatMoney(selectedReturn.totalRefundAmount)}
                  </span>
                </div>
              </div>

              <div className="sp-basket-list sp-basket-list--readonly sp-scrollbar-hidden">
                <div className="sp-basket-row sp-basket-row--header" aria-hidden="true">
                  <span>{t("commerceCommon.itemHeader")}</span>
                  <span>{t("commerceCommon.qty")}</span>
                  <span>{t("commerceCommon.price")}</span>
                  <span>{t("commerceCommon.refund")}</span>
                </div>
                {(selectedReturn.lines || []).map((line) => {
                  const metaHint = [line.sku, line.variantBarcode].filter(Boolean).join(" · ");
                  return (
                    <div className="sp-basket-row" key={line.id}>
                      <div className="sp-basket-row__item">
                        <SaleItemLabel
                          productTitle={line.productTitle}
                          variantTitle={line.variantTitle}
                          optionSummary={line.optionSummary}
                          className="sp-item-label sp-truncate"
                        />
                        {metaHint ? (
                          <span className="sp-return-row__meta" title={metaHint}>
                            {metaHint}
                          </span>
                        ) : null}
                        {line.reason ? (
                          <span className="sp-text-sm sp-text-dim">{line.reason}</span>
                        ) : null}
                      </div>
                      <span className="sp-basket-row__qty">{line.returnedQuantity}</span>
                      <span className="sp-basket-row__price">
                        {formatMoney(line.finalUnitPriceAmount ?? line.unitPriceAmount)}
                      </span>
                      <span className="sp-basket-row__total">
                        {formatMoney(line.refundAmount)}
                      </span>
                    </div>
                  );
                })}
              </div>

              {selectedReturn.note ? (
                <p className="sp-detail-note sp-text-sm sp-text-muted">
                  {selectedReturn.note}
                </p>
              ) : null}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default RecentReturnsPage;
